import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API, formatCurrency, STATUS_LABELS, INVOICE_STATUS_LABELS } from '../lib/utils';
import { toast } from 'sonner';
import { Plus, AlertCircle, CheckCircle, Clock, BarChart3, Lock } from 'lucide-react';

const QUOTE_COLORS = {
  brouillon: 'bg-white/10 text-white/60 border border-white/20',
  envoye:    'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  accepte:   'bg-green-500/20 text-green-400 border border-green-500/30',
  refuse:    'bg-red-500/20 text-red-400 border border-red-500/30',
  expire:    'bg-red-500/20 text-red-400 border border-red-500/30',
};

const INVOICE_COLORS = {
  brouillon: 'bg-white/10 text-white/60 border border-white/20',
  envoyee:   'bg-blue-500/20 text-blue-400 border border-blue-500/30',
  en_retard: 'bg-red-500/20 text-red-400 border border-red-500/30',
  payee:     'bg-green-500/20 text-green-400 border border-green-500/30',
};

const DashboardPage = () => {
  // Sprint 2 task 5 : on récupère subscription + canWrite du contexte (au lieu de re-fetcher localement)
  const { company, token, subscription, canWrite } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  // Fetch les détails Stripe (pour la date de renouvellement) seulement si actif
  useEffect(() => {
    if (subscription?.subscription_status === 'active') {
      fetchSubscriptionDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.subscription_status]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      // Sprint 2 task 5 : l'intercepteur global gère déjà subscription_required et readonly_mode
      // Ici on ne traite que les autres erreurs (réseau, 500, etc.)
      if (error.response?.status !== 403) {
        toast.error('Erreur lors du chargement du tableau de bord');
      }
    }
    setLoading(false);
  };

  const fetchSubscriptionDetails = async () => {
    try {
      const detailsResponse = await axios.get(`${API}/stripe/subscription-details`);
      setSubscriptionDetails(detailsResponse.data);
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    }
  };

  // Sprint 2 task 5 : handler pour le bouton "Nouveau devis"
  // Si l'user est expired/canceled, on redirige vers /subscription au lieu d'aller sur /quotes/new
  const handleNewQuote = () => {
    if (canWrite()) {
      navigate('/quotes/new');
    } else {
      toast.info('Activez votre abonnement pour créer un nouveau devis', {
        action: {
          label: 'Activer',
          onClick: () => navigate('/subscription')
        }
      });
      navigate('/subscription');
    }
  };

  const formatDateShort = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="page-container p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-28 bg-muted rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const amountToCollect = dashboard?.amount_to_collect || 0;
  const amountCollectedMonth = dashboard?.amount_collected_month || 0;
  const overdueInvoices = dashboard?.overdue_invoices || 0;

  // Devis à afficher : envoyé, accepté, refusé, expiré
  const paidInvoices = (dashboard?.recent_invoices || []).filter(i => i.status === 'payee');

  const visibleQuotes = (dashboard?.recent_quotes || [])
    .filter(q => {
      if (!['brouillon', 'envoye', 'accepte'].includes(q.status)) return false;
      if (q.status === 'accepte' && paidInvoices.some(i => i.client_name === q.client_name && i.total_ttc === q.total_ttc)) return false;
      return true;
    })
    .sort((a, b) => {
      const priority = { refuse: 0, expire: 1, envoye: 2, accepte: 3, brouillon: 4 };
      return (priority[a.status] ?? 9) - (priority[b.status] ?? 9);
    });

  // Factures à afficher : brouillon, envoyée, en retard
  const visibleInvoices = (dashboard?.recent_invoices || [])
    .filter(i => ['brouillon', 'envoyee', 'en_retard'].includes(i.status))
    .sort((a, b) => {
      const priority = { en_retard: 0, envoyee: 1, brouillon: 2 };
      return (priority[a.status] ?? 9) - (priority[b.status] ?? 9);
    });

  // Sprint 2 task 5 : raccourci local pour le rendu conditionnel
  const isExpired = subscription?.subscription_status === 'expired' || subscription?.subscription_status === 'canceled';

  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Tableau de bord — {company ? company.name : 'DevisFast'}
          </h1>
          <Button
            onClick={handleNewQuote}
            className="h-10 px-5 bg-primary text-primary-foreground hover:bg-accent font-semibold rounded-full"
            data-testid="new-quote-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau devis
          </Button>
        </div>

        {/* Company warning */}
        {!company && !isExpired && (
          <Card className="bg-blue-500/10 border-blue-500/30 mb-4">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-blue-400 font-medium">Profil entreprise incomplet</p>
                <p className="text-sm text-muted-foreground">Configurez votre entreprise pour créer des devis professionnels</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/profile')} className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20" data-testid="setup-company-btn">
                Configurer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Sprint 2 task 5 : Bandeau EXPIRED — lecture seule, invitation à activer */}
        {isExpired && (
          <Card
            className="bg-orange-500/10 border-orange-500/40 mb-4 cursor-pointer hover:bg-orange-500/15 transition-colors"
            onClick={() => navigate('/subscription')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <Lock className="w-5 h-5 text-orange-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-orange-400 font-medium">Compte en lecture seule</p>
                <p className="text-sm text-muted-foreground">
                  Vous gardez accès à tous vos anciens devis et factures. Activez votre abonnement pour reprendre la création.
                </p>
              </div>
              <Button
                variant="outline" size="sm"
                onClick={(e) => { e.stopPropagation(); navigate('/subscription'); }}
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                data-testid="activate-subscription-btn"
              >
                Activer mon abonnement
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Trial banner */}
        {subscription?.subscription_status === 'trialing' && subscription?.days_remaining !== null && (
          <Card
            className={`mb-4 cursor-pointer transition-all hover:scale-[1.01] ${
              subscription.days_remaining < 2
                ? 'bg-red-500/10 border-red-500/30'
                : subscription.days_remaining < 5
                  ? 'bg-orange-500/10 border-orange-500/30'
                  : 'bg-blue-500/10 border-blue-500/30'
            }`}
            onClick={() => navigate('/subscription')}
          >
            <CardContent className="p-4 flex items-center gap-3">
              <Clock className={`w-5 h-5 flex-shrink-0 ${subscription.days_remaining < 2 ? 'text-red-500' : subscription.days_remaining < 5 ? 'text-orange-500' : 'text-blue-400'}`} />
              <div className="flex-1">
                <p className={`font-medium ${subscription.days_remaining < 2 ? 'text-red-500' : subscription.days_remaining < 5 ? 'text-orange-500' : 'text-blue-400'}`}>
                  Essai gratuit — il vous reste {subscription.days_remaining} jour{subscription.days_remaining > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-muted-foreground">Après votre essai, continuez pour 9,99€/mois seulement</p>
              </div>
              <Button
                variant="outline" size="sm"
                onClick={(e) => { e.stopPropagation(); navigate('/subscription'); }}
                className={`${subscription.days_remaining < 2 ? 'border-red-500/50 text-red-500' : subscription.days_remaining < 5 ? 'border-orange-500/50 text-orange-500' : 'border-blue-500/50 text-blue-400'}`}
                data-testid="view-subscription-btn"
              >
                Voir mon abonnement
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Active subscription banner */}
        {subscription?.subscription_status === 'active' && (
          <Card className="bg-green-500/10 border-green-500/30 mb-4 cursor-pointer hover:bg-green-500/15 transition-colors" onClick={() => navigate('/subscription')}>
            <CardContent className="p-3 flex items-center gap-3">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <p className="text-green-500 text-sm font-medium flex-1">
                Abonnement actif · 9,99€/mois
                {subscriptionDetails?.next_renewal_date && (
                  <span className="text-green-400/80 font-normal"> · Renouvellement le {formatDateShort(subscriptionDetails.next_renewal_date)}</span>
                )}
              </p>
            </CardContent>
          </Card>
        )}

        {/* KPI Grid — 3 KPIs simples */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          <Card className="bg-card border-green-500/20 cursor-pointer hover:border-green-500/40 transition-colors" onClick={() => navigate('/invoices?status=payee')}>
            <CardContent className="p-2 md:p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1 leading-tight">Encaissé</p>
              <p className={`text-base md:text-2xl font-bold truncate ${amountCollectedMonth > 0 ? 'text-green-400' : 'text-foreground'}`}>{formatCurrency(amountCollectedMonth)}</p>
              <p className="text-xs text-green-400/70 mt-1 hidden md:block">✓ factures payées</p>
            </CardContent>
          </Card>

          <Card className="bg-card border-blue-500/20 cursor-pointer hover:border-blue-500/40 transition-colors" onClick={() => navigate('/invoices?status=envoyee')}>
            <CardContent className="p-2 md:p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1 leading-tight">À encaisser</p>
              <p className={`text-base md:text-2xl font-bold truncate ${amountToCollect > 0 ? 'text-blue-400' : 'text-foreground'}`}>{formatCurrency(amountToCollect)}</p>
              <p className="text-xs text-blue-400/70 mt-1 hidden md:block">↗ factures envoyées</p>
            </CardContent>
          </Card>

          <Card className={`bg-card cursor-pointer transition-colors ${overdueInvoices > 0 ? 'border-red-500/40 hover:border-red-500/60' : 'border-white/10 hover:border-white/20'}`} onClick={() => navigate('/invoices?status=en_retard')}>
            <CardContent className="p-2 md:p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1 leading-tight">En retard</p>
              <p className={`text-base md:text-2xl font-bold ${overdueInvoices > 0 ? 'text-red-500' : 'text-foreground'}`}>{overdueInvoices}</p>
              <p className="text-xs text-red-400/70 mt-1 hidden md:block">{overdueInvoices > 0 ? '⚠ à relancer' : '— aucune'}</p>
            </CardContent>
          </Card>
        </div>

        {/* Devis + Factures — tout cliquable */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">

          {/* Devis */}
          <Card className="bg-card border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-sm uppercase tracking-wider">Devis</h2>
                <Link to="/quotes" className="text-primary text-xs hover:underline">Voir tout</Link>
              </div>
              {visibleQuotes.length === 0 ? (
                <div className="flex items-center gap-2 py-6 justify-center text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm">Aucun devis en cours</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleQuotes.map((quote) => (
                    <div
                      key={quote.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/quotes/${quote.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{quote.client_name}</p>
                        <p className="text-xs text-muted-foreground">{quote.quote_number} · {formatCurrency(quote.total_ttc)}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${QUOTE_COLORS[quote.status] || ''}`}>
                        {STATUS_LABELS[quote.status]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Factures */}
          <Card className="bg-card border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-sm uppercase tracking-wider">Factures</h2>
                <Link to="/invoices" className="text-primary text-xs hover:underline">Voir tout</Link>
              </div>
              {visibleInvoices.length === 0 ? (
                <div className="flex items-center gap-2 py-6 justify-center text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm">Aucune facture en attente</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleInvoices.map((invoice) => (
                    <div
                      key={invoice.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{invoice.client_name}</p>
                        <p className="text-xs text-muted-foreground">{invoice.invoice_number} · {formatCurrency(invoice.total_ttc)}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${INVOICE_COLORS[invoice.status] || ''}`}>
                        {INVOICE_STATUS_LABELS[invoice.status]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Graphique CA */}
        {dashboard?.monthly_revenue && (
          <Card className="bg-card border-white/10 mb-8">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <BarChart3 className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wider font-semibold">Chiffre d'affaires — 6 derniers mois</span>
              </div>
              <div className="flex items-end justify-between h-28 gap-2">
                {dashboard.monthly_revenue.map((month, idx) => {
                  const maxAmount = Math.max(...dashboard.monthly_revenue.map(m => m.amount), 1);
                  const height = (month.amount / maxAmount) * 100;
                  const monthLabel = new Date(month.month + '-01').toLocaleDateString('fr-FR', { month: 'short' });
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-muted-foreground">{month.amount > 0 ? formatCurrency(month.amount) : ''}</span>
                      <div className="w-full bg-primary/70 rounded-t transition-all duration-500 min-h-[4px]" style={{ height: `${Math.max(height, 4)}%` }} />
                      <span className="text-xs text-muted-foreground capitalize">{monthLabel}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Accès rapides */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1 border-white/10 hover:bg-muted" onClick={() => navigate('/clients')}>
            <span className="text-xl">👥</span><span className="text-xs">Clients</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1 border-white/10 hover:bg-muted" onClick={() => navigate('/services')}>
            <span className="text-xl">🔧</span><span className="text-xs">Services</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1 border-white/10 hover:bg-muted" onClick={() => navigate('/quotes')}>
            <span className="text-xl">📄</span><span className="text-xs">Devis</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1 border-white/10 hover:bg-muted" onClick={() => navigate('/invoices')}>
            <span className="text-xl">🧾</span><span className="text-xs">Factures</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex flex-col items-center gap-1 border-white/10 hover:bg-muted col-span-2 md:col-span-1" onClick={() => navigate('/profile')}>
            <span className="text-xl">⚙️</span><span className="text-xs">Profil</span>
          </Button>
        </div>

      </div>
    </div>
  );
};

export default DashboardPage;
